import { useTranslate } from "ra-core";

import { cn } from "@/lib/utils";

/** Renewal state computed by companies_summary (migration 20261002120000). */
export type RenewalStatus = "renewed" | "down" | "new" | "pending" | "none";

const TONE: Record<RenewalStatus, string> = {
  renewed: "bg-good-tint text-good",
  down: "bg-warn-tint text-warn",
  new: "bg-accent text-accent-text",
  pending: "bg-bad-tint text-bad",
  none: "bg-neutral-tint text-neutral-state",
};

/** Coloured state pill: the colour carries the meaning, the label names it. */
export const StatePill = ({
  tone,
  children,
  className,
}: {
  tone: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium before:size-1.5 before:rounded-full before:bg-current before:content-['']",
      tone,
      className,
    )}
  >
    {children}
  </span>
);

export const RenewalPill = ({ status }: { status?: RenewalStatus | null }) => {
  const translate = useTranslate();
  const value = status ?? "none";
  return (
    <StatePill tone={TONE[value]}>
      {translate(`crm.construnario.renewal.${value}`)}
    </StatePill>
  );
};

/** Percentage change of the current year against the previous one. */
export const AmountDelta = ({
  current,
  previous,
}: {
  current?: number | string | null;
  previous?: number | string | null;
}) => {
  if (!current || !previous) return null;
  const change = Math.round((Number(current) / Number(previous) - 1) * 100);
  if (change === 0) return null;
  return (
    <span
      className={cn(
        "ml-1.5 text-xs tabular-nums",
        change > 0 ? "text-good" : "text-bad",
      )}
    >
      {change > 0 ? "+" : ""}
      {change}%
    </span>
  );
};
